import React, { useEffect, useContext } from "react";
import { useLocation, useHistory } from "react-router-dom";
import DatePicker from "./DatePicker";
import { getDateDB, getTomorrowDate } from "../utils/date";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import availabilityService from "../services/availabilityService";
import Context from "../utils/context";
import { gtm } from "../utils/gtm";

const MAX_GUESTS = 15;

function GuestCounter({ value, onChange, min = 0, max = MAX_GUESTS }) {
  return (
    <div className="guest-counter">
      <button
        type="button"
        className="guest-counter__btn guest-counter__btn--minus"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label="Decrease"
      >
        −
      </button>
      <span className="guest-counter__value">{value}</span>
      <button
        type="button"
        className="guest-counter__btn guest-counter__btn--plus"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}

export default function AvailabilityPicker({
  setBookingData,
  setRoomsToDisplay,
  setIsRoomsLoading,
  setShowBookingView,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const { availabilityData: data, setAvailabilityData: setData } =
    useContext(Context);

  useEffect(() => {
    if (location.pathname !== "/rooms") {
      return;
    }

    const current = new Date().toLocaleDateString();
    const tomorrow = getTomorrowDate().toLocaleDateString();

    if (
      current !== data.startDate?.toLocaleDateString() &&
      tomorrow !== data.endDate?.toLocaleDateString()
    ) {
      onSubmit({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const [data, setData] = useState({
  //   startDate: new Date(),
  //   endDate: getTomorrowDate(),
  //   adults: { value: 1, label: "1" },
  //   children: { value: 0, label: "0" },
  // });

  const handleChange = (field, value) => {
    let dataCopy = { ...data };
    dataCopy[field] = value;
    setData({ ...dataCopy });
    if (field === "adults" || field === "children") {
      setBookingData?.({ ...dataCopy });
    }
  };

  const setGuestCount = (field, count) => {
    handleChange(field, { value: count, label: String(count) });
  };

  const getAvailability = async (data) => {
    let dataCopy = { ...data };
    dataCopy["children"] = data.children.value;
    dataCopy["adults"] = data.adults.value;
    dataCopy["startDate"] = getDateDB(data.startDate);
    dataCopy["endDate"] = getDateDB(data.endDate);
    const res = await availabilityService.getAvailabilityForPeriod(dataCopy);
    gtm.checkAvailability({
      checkIn: dataCopy.startDate,
      checkOut: dataCopy.endDate,
      adults: dataCopy.adults,
      children: dataCopy.children,
    });
    setShowBookingView?.(true);
    setRoomsToDisplay?.(res.data);
    setIsRoomsLoading?.(false);
  };

  useEffect(() => {
    if (location.pathname !== "/rooms" || !location.state) {
      return;
    }

    getAvailability(location.state.data);
    setData(location.state.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const currentLocation = history.location.pathname;

    if (data.adults.value === 0) {
      toast.error(t("choose-adults"));
      return;
    }
    const startDate = new Date(data.startDate.setHours(0, 0, 0, 0)).getTime();
    const endDate = new Date(data.endDate.setHours(0, 0, 0, 0)).getTime();

    if (startDate >= endDate) {
      toast.error(t("invalid-period"));
      return;
    }

    if (currentLocation === "/home" || currentLocation === "/") {
      history.push({
        pathname: "/rooms",
        state: {
          data,
        },
      });
    } else {
      setIsRoomsLoading?.(true);
      await getAvailability(data);
      setIsRoomsLoading?.(false);
      setBookingData?.(data);
    }
  };

  return (
    <div className="check-availability">
      <div className="auto-container">
        <form
          className="form availability-form"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="left-side">
            <ul className="availability-fields">
              <li className="availability-field availability-field--date">
                <DatePicker
                  date={data.startDate}
                  onChange={(date) => handleChange("startDate", date)}
                  placeholder={`Arrival Date`}
                />
              </li>
              <li className="availability-field availability-field--date">
                <DatePicker
                  date={data.endDate}
                  onChange={(date) => handleChange("endDate", date)}
                  placeholder={`Departure Date`}
                />
              </li>
              <li className="availability-field availability-field--guests">
                <label htmlFor="adults-counter">{t("adults")}</label>
                <GuestCounter
                  value={data.adults?.value ?? 1}
                  min={1}
                  onChange={(count) => setGuestCount("adults", count)}
                />
              </li>
              <li className="availability-field availability-field--guests">
                <label htmlFor="children-counter">{t("children")}</label>
                <GuestCounter
                  value={data.children?.value ?? 0}
                  min={0}
                  onChange={(count) => setGuestCount("children", count)}
                />
              </li>
            </ul>
          </div>
          <div className="right-side">
            <button onClick={onSubmit} type="submit">
              {t("check-availability")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
